import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, EnvelopeSimple, FileText, MapPin, Phone, UsersThree } from "@phosphor-icons/react";
import { Header, MobileNav } from "@/components/Header";
import api from "@/lib/api";
import { useBuilding } from "@/context/BuildingContext";
import { CONTACT_ROLES, docCategoryLabel, formatDate } from "@/lib/constants";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function Infos() {
  const BUILDING = useBuilding();
  const [contacts, setContacts] = useState(null);
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="infos-page">
      <Header />
      <main className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{BUILDING.name}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl">Informations & contacts</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Les personnes à contacter et les documents officiels de votre copropriété.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8 flex items-center gap-4 rounded-3xl border border-black/5 bg-primary p-6 text-primary-foreground shadow-[0_14px_40px_rgba(24,56,43,0.25)]"
          data-testid="infos-building-card"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15">
            <MapPin size={24} weight="duotone" />
          </span>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">{BUILDING.name}</p>
            {BUILDING.address && <p className="font-display text-sm font-bold tracking-tight opacity-90">{BUILDING.address}</p>}
          </div>
        </motion.div>

        {contacts === null && (
          <div className="mt-8 space-y-4" data-testid="contacts-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-3xl bg-black/5" />
            ))}
          </div>
        )}

        {contacts !== null && contacts.length === 0 && (
          <p className="mt-8 rounded-3xl border border-black/5 bg-white p-10 text-center text-sm text-muted-foreground" data-testid="contacts-empty">
            Les contacts seront bientôt renseignés par le gestionnaire.
          </p>
        )}

        {CONTACT_ROLES.map(({ key, label, Icon }, gi) => {
          const people = (contacts || []).filter((c) => c.role === key);
          if (!people.length) return null;
          return (
            <motion.section
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 + gi * 0.1 }}
              className="mt-10"
              data-testid={`contacts-group-${key}`}
            >
              <h2 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
                <Icon size={22} weight="duotone" className="text-primary" />
                {label}
              </h2>
              <div className="mt-4 space-y-3">
                {people.map((c, i) => (
                  <div
                    key={c.id}
                    className="rounded-3xl border border-black/5 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                    data-testid={`contact-card-${key}-${i}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Icon size={20} weight="duotone" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base font-bold tracking-tight">{c.name}</p>
                        {c.detail && <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>}
                      </div>
                    </div>
                    {(c.phone || c.email) && (
                      <div className="mt-4 flex flex-wrap items-center gap-2 sm:justify-end">
                        {c.phone && (
                          <a
                            href={`tel:${c.phone.replace(/\s/g, "")}`}
                            data-testid={`contact-phone-${c.id}`}
                            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
                          >
                            <Phone size={13} weight="fill" />
                            {c.phone}
                          </a>
                        )}
                        {c.email && (
                          <a
                            href={`mailto:${c.email}`}
                            data-testid={`contact-mail-${c.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
                          >
                            <EnvelopeSimple size={13} weight="bold" />
                            E-mail
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.section>
          );
        })}

        {docs !== null && docs.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10"
            data-testid="documents-section"
          >
            <h2 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
              <FileText size={22} weight="duotone" className="text-primary" />
              Documents officiels
            </h2>
            <div className="mt-4 space-y-3">
              {docs.map((d, i) => (
                <div
                  key={d.id}
                  className="flex items-center gap-4 rounded-3xl border border-black/5 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                  data-testid={`document-card-${i}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF0D4] text-[#B25E02]">
                    <FileText size={20} weight="duotone" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-base font-bold tracking-tight">{d.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {docCategoryLabel(d.category)} · {formatDate(d.created_at)} {d.size ? `· ${formatSize(d.size)}` : ""}
                    </p>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`}
                    data-testid={`document-download-${i}`}
                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
                  >
                    <Download size={14} weight="bold" />
                    <span className="hidden sm:inline">Télécharger</span>
                  </a>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {contacts !== null && contacts.length > 0 && (
          <p className="mt-10 flex items-center gap-2 text-xs text-muted-foreground">
            <UsersThree size={16} weight="duotone" />
            Informations mises à jour par le gestionnaire de la copropriété.
          </p>
        )}
      </main>
      <MobileNav />
    </div>
  );
}
